import * as XYFlow from '@xyflow/react';
import { Position, NodeProps } from '@xyflow/react';
import {
    Play,
    Wand2,
    Globe,
    GitBranch,
    Send,
    Boxes,
    Image as ImageIcon,
} from 'lucide-react';
import {
    WorkflowNodeType,
    WorkflowNodeConfig,
    BranchData,
    EmitCodesData,
    HttpRequestData,
    TransformData,
} from './types';

// @xyflow/react 12.3.2 ships a duplicate `Handle` export (value + type), which
// masks the component value in its typings. Pull it off the namespace so TS
// keeps the runtime component.
const Handle: any = (XYFlow as any).Handle;

export interface RFNodeData {
    nodeType: WorkflowNodeType;
    nodeKey: string;
    label?: string;
    config: WorkflowNodeConfig;
    [key: string]: unknown;
}

const ICONS: Record<WorkflowNodeType, React.ReactNode> = {
    trigger: <Play size={14} />,
    transform: <Wand2 size={14} />,
    httpRequest: <Globe size={14} />,
    lookupMedia: <ImageIcon size={14} />,
    branch: <GitBranch size={14} />,
    emitCodes: <Send size={14} />,
};

const TITLES: Record<WorkflowNodeType, string> = {
    trigger: 'Trigger',
    transform: 'Transform',
    httpRequest: 'HTTP request',
    lookupMedia: 'Look up media',
    branch: 'Branch',
    emitCodes: 'Emit codes',
};

function Shell({
    data,
    selected,
    children,
    showTarget = true,
    showSource = true,
}: {
    data: RFNodeData;
    selected?: boolean;
    children?: React.ReactNode;
    showTarget?: boolean;
    showSource?: boolean;
}) {
    return (
        <div
            className={`min-w-[180px] rounded-lg border bg-white px-3 py-2 shadow-sm ${
                selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
            }`}
        >
            {showTarget && (
                <Handle type="target" position={Position.Left} />
            )}
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <span className="text-blue-600">{ICONS[data.nodeType]}</span>
                {data.label || TITLES[data.nodeType]}
            </div>
            <div className="mt-0.5 font-mono text-[10px] text-gray-400">
                {data.nodeKey}
            </div>
            {children}
            {showSource && <Handle type="source" position={Position.Right} />}
        </div>
    );
}

function TriggerNode({ data, selected }: NodeProps) {
    return (
        <Shell data={data as RFNodeData} selected={selected} showTarget={false} />
    );
}

function TransformNode({ data, selected }: NodeProps) {
    const config = (data as RFNodeData).config as TransformData;
    const count = config.assignments?.length ?? 0;
    return (
        <Shell data={data as RFNodeData} selected={selected}>
            <div className="mt-1 text-xs text-gray-500">
                {count} value{count === 1 ? '' : 's'}
            </div>
        </Shell>
    );
}

function HttpNode({ data, selected }: NodeProps) {
    const config = (data as RFNodeData).config as HttpRequestData;
    return (
        <Shell data={data as RFNodeData} selected={selected}>
            <div className="mt-1 truncate text-xs text-gray-500">
                {config.method ?? 'GET'} {config.url ? '· configured' : ''}
            </div>
        </Shell>
    );
}

function EmitNode({ data, selected }: NodeProps) {
    const config = (data as RFNodeData).config as EmitCodesData;
    const count = config.codes?.length ?? 0;
    return (
        <Shell data={data as RFNodeData} selected={selected} showSource={false}>
            <div className="mt-1 text-xs text-gray-500">
                {count} code{count === 1 ? '' : 's'}
            </div>
        </Shell>
    );
}

function BranchNode({ data, selected }: NodeProps) {
    const config = (data as RFNodeData).config as BranchData;
    const conditions = config.conditions ?? [];
    // One labeled source handle per condition, plus an "else" handle.
    const handles = [
        ...conditions.map((c) => ({ id: c.id, label: c.label || c.id })),
        { id: 'else', label: 'else' },
    ];
    return (
        <div
            className={`min-w-[200px] rounded-lg border bg-white px-3 py-2 shadow-sm ${
                selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
            }`}
        >
            <Handle type="target" position={Position.Left} />
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <span className="text-blue-600">{ICONS.branch}</span>
                {(data as RFNodeData).label || TITLES.branch}
            </div>
            <div className="mt-0.5 font-mono text-[10px] text-gray-400">
                {(data as RFNodeData).nodeKey}
            </div>
            <div className="mt-2 space-y-3">
                {handles.map((h, i) => (
                    <div
                        key={h.id}
                        className="relative flex justify-end pr-1 text-[11px] text-gray-600"
                    >
                        {h.label}
                        <Handle
                            type="source"
                            position={Position.Right}
                            id={h.id}
                            style={{ top: `${48 + i * 22}px` }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

// Fallback renderer for step types registered on the backend that the editor
// has no dedicated component for. Keeps newly-added steps usable on the canvas.
export function GenericNode({ data, selected }: NodeProps) {
    const d = data as RFNodeData;
    return (
        <div
            className={`min-w-[180px] rounded-lg border bg-white px-3 py-2 shadow-sm ${
                selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
            }`}
        >
            <Handle type="target" position={Position.Left} />
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <span className="text-blue-600">
                    <Boxes size={14} />
                </span>
                {d.label || d.nodeType}
            </div>
            <div className="mt-0.5 font-mono text-[10px] text-gray-400">
                {d.nodeKey} · {d.nodeType}
            </div>
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

export const knownNodeTypes = {
    trigger: TriggerNode,
    transform: TransformNode,
    httpRequest: HttpNode,
    branch: BranchNode,
    emitCodes: EmitNode,
};

export const nodeTypes = knownNodeTypes;
